import {
  PromptModelConfig,
  ApiLibraryModelVersion,
  InitialLibraryModelVersionCreate,
  NextLibraryModelVersionCreate,
} from '~/api/sylvanus/typedefs.autogen';
import { ChartType, Datum } from '~/eds/src/components/Chart/types';
import { SearchQuery } from '~/features/advanced-search/types';
import { FieldModelConfigState } from '~/features/x-ray/fields/types';
import { LibraryModelVersion } from '~/features/x-ray/library/types';
import { intlService } from '~/i18n/IntlService';
import { sylvanus } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { withQueryFormat } from './searchScopeUtils';
import {
  mapFieldClassificationConfigAttributes,
  mapLibraryModelVersion,
  mapModelConfigAttributes,
} from './transformers';

interface RequestParams {
  changelog: string;
  config: FieldModelConfigState | PromptModelConfig;
  description: string;
  libraryModelId: Nullable<Uuid>;
  name: string;
  sampleOutputs: { buckets?: Datum[]; text?: string; type?: ChartType };
  tags: string[];
  updateType: 'major' | 'minor' | 'patch';
  instructions?: string;
  internal?: string;
  query?: SearchQuery;
}

type Request = InitialLibraryModelVersionCreate | NextLibraryModelVersionCreate;

type Response = ApiLibraryModelVersion;

/** Create a new library model if no `libraryModelId` is provided,
 * otherwise create a new version for the existing library model. */
export const saveLibraryModel = async ({
  changelog,
  config,
  description,
  instructions,
  internal,
  libraryModelId,
  name,
  query,
  sampleOutputs,
  tags,
  updateType,
}: RequestParams): Promise<LibraryModelVersion> => {
  if (!libraryModelId && !config.field) {
    throw new Error(
      intlService.getIntl().formatMessage({
        id: 'saveLibraryModel.missingField',
        defaultMessage: 'Cannot save library model: Missing field.',
        description: 'Error message when field is missing for library model',
      }),
    );
  }

  const configuration = libraryModelId
    ? ({
        ...config,
        instructions,
        // use provided value or fallback on existing one, internal is required by BE
        internal: internal ?? config.internal,
        ...(query
          ? {
              scope: {
                data: {
                  attributes: {
                    filters: [],
                    query: withQueryFormat(query),
                    store_as_recent_search: false,
                  },
                },
                type: 'document',
              },
            }
          : {}),
      } as PromptModelConfig)
    : {
        field_name: config.field?.label,
        instructions: config.instructions,
        internal: config.internal,
        scope: {
          data: {
            attributes: {
              filters: [],
              query: withQueryFormat(config.query),
              store_as_recent_search: false,
            },
          },
          type: 'document',
        },
        ...mapModelConfigAttributes(config as FieldModelConfigState),
        ...mapFieldClassificationConfigAttributes(
          config as FieldModelConfigState,
        ),
      };

  const initialRequest = {
    changelog_entry: changelog,
    configuration,
    description,
    name,
    sample_outputs: {
      text: sampleOutputs.text,
      dashboard: sampleOutputs.type
        ? { buckets: sampleOutputs.buckets, type: sampleOutputs.type }
        : undefined,
    },
    target: {
      entity_type: 'field',
      value_type: configuration.output_type,
    },
    tags: tags.map((tag) => ({ tag_type: tag, tag_value: tag })),
  };

  const endpoint = libraryModelId
    ? `/library/model/${libraryModelId}/version`
    : '/library/model';

  const request: Request = libraryModelId
    ? ({
        ...initialRequest,
        update_type: updateType,
      } as NextLibraryModelVersionCreate)
    : (initialRequest as InitialLibraryModelVersionCreate);

  const response: Response = await sylvanus.post(endpoint, request);

  return mapLibraryModelVersion(response);
};
